import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const IconRegistryContext = createContext({ icons: {}, loaded: false, refresh: () => {} });

export function IconRegistryProvider({ children }) {
  const [icons, setIcons] = useState({});
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/status-icons");
      setIcons(data.icons || {});
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <IconRegistryContext.Provider value={{ icons, loaded, refresh }}>
      {children}
    </IconRegistryContext.Provider>
  );
}

export function useStatusIcons() {
  return useContext(IconRegistryContext);
}

/**
 * StatusIcon, renders a stained-glass PNG from the Media Library by status_key.
 * Falls back gracefully (small text label) if the icon hasn't been uploaded yet.
 *
 * <StatusIcon name="grower.active" size={24} />
 * <StatusIcon name="delivery.packed" size={32} fallback={<Clock />} />
 */
export function StatusIcon({ name, size = 20, className = "", title, fallback = null, alt }) {
  const { icons, loaded } = useStatusIcons();
  const icon = icons[name];
  if (!icon) {
    if (!loaded) {
      return <span style={{ width: size, height: size, display: "inline-block" }} aria-hidden />;
    }
    return fallback;
  }
  return (
    <img
      src={icon.url}
      alt={alt || icon.name || name}
      title={title || icon.name || name}
      width={size}
      height={size}
      className={className}
      style={{ objectFit: "contain", display: "inline-block", verticalAlign: "middle" }}
      data-testid={`status-icon-${name}`}
    />
  );
}

/**
 * StickerGrid, pulls non-archived media from a category and renders a clickable PNG grid.
 *
 * <StickerGrid category="portal_navigation" onSelect={(a) => ...} />
 */
export function StickerGrid({ category, onSelect, columns = 4, badge }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/media/by-category/${category}`);
        setItems(data.assets || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [category]);

  if (loading) return <div className="font-serif italic text-[var(--bloom-ink-muted)] py-12 text-center">Loading…</div>;
  if (!items.length) {
    return (
      <div className="bloom-card p-10 text-center" data-testid={`sticker-grid-empty-${category}`}>
        <div className="bloom-label mb-2">No stickers yet</div>
        <p className="font-serif italic text-sm text-[var(--bloom-ink-soft)] max-w-md mx-auto">
          Upload PNGs into the <em>{category.replace(/_/g, " ")}</em> category from the Media Library and they appear here automatically.
        </p>
      </div>
    );
  }
  return (
    <div className={`grid gap-4 grid-cols-2 md:grid-cols-${Math.min(columns, 4)} lg:grid-cols-${columns}`} data-testid={`sticker-grid-${category}`}>
      {items.map((a) => (
        <button
          key={a.id}
          onClick={() => onSelect?.(a)}
          className="group relative bloom-card p-4 hover:shadow-lg transition flex flex-col items-center text-center"
          data-testid={`sticker-${a.id}`}
        >
          {badge && badge(a) && (
            <span className="absolute top-2 right-2 bg-[var(--bloom-accent)] text-white text-[10px] font-serif italic px-2 py-0.5 rounded-full">{badge(a)}</span>
          )}
          <div className="w-full aspect-square flex items-center justify-center mb-3 transition group-hover:scale-[1.05]">
            <img src={a.data_url} alt={a.name} className="max-w-full max-h-full object-contain" />
          </div>
          <div className="font-serif text-base leading-tight">{a.name}</div>
          {a.subcategory && <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--bloom-ink-muted)] mt-1">{a.subcategory}</div>}
        </button>
      ))}
    </div>
  );
}
